<script setup>
import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel'
import { vElementSize } from '@vueuse/components'
import 'vue3-carousel/dist/carousel.css'

defineProps({ blok: Object, slideItem: Array, cardItem: Array, cardTitle: String, cardSubtitle: Object })

function cardContent(story) {
  return renderRichText(story)
}

const isClient = ref(false)
const currentSlide = ref(0)
const carousel = ref(null)
const targetIsVisible = ref(false)
const getWidth = ref(0)
const getHeight = ref(680)
const { categories, fetchCategories } = useNewsCategories()
const { fetchAll, get_all } = useArticles()

useCarouselInert(carousel)

function handleClick(e) {
  e.preventDefault()
}

function onResize({ width, height }) {
  getWidth.value = width
  getHeight.value = height
}

onMounted(() => {
  if (import.meta.client) {
    isClient.value = true
    targetIsVisible.value = true
    fetchCategories()
    fetchAll()
  }
})
</script>

<template>
  <div class="hero-container relative isolate min-h-screen flex items-start overflow-hidden pt-[40px] xl:pt-[50px]" @contextmenu.prevent="handleClick">
    <div class="relative z-10 container">
      <div class="relative z-10 min-h-0 w-full flex flex-col gap-8 pb-4 lg:h-[720px] xl:h-[705px] lg:flex-row lg:items-stretch lg:justify-between xl:gap-12 md:pb-5">
        <div
          class="relative z-10 min-h-0 w-full max-w-[900px] flex flex-col justify-between lg:h-full lg:w-[65%] lg:overflow-hidden"
        >
          <Carousel ref="carousel" v-model="currentSlide" class="carousel-custom-navigation carousel-custom-pagination carousel-hero relative w-full" snap-align="center" :transition="500" :autoplay="5000" :wrap-around="isClient">
            <Slide v-for="content in slideItem" :key="content._uid" class="pb-custom-lg">
              <h1
                class="mr-auto text-left text-md text-secondary-red-100 font-semibold uppercase md:text-lg"
              >
                {{ content.header }}
              </h1>
              <h1
                class="line-clamp-2 mb-custom-md mr-auto text-left text-3xl text-white font-bold uppercase md:text-5xl sm:text-4xl xl:text-6xl !leading-[1]"
              >
                {{ content.title }}
              </h1>
              <div class="hero-content mb-custom-md mr-auto text-left" v-html="cardContent(content.content)" />
              <ul v-if="content.actions.length" class="mr-auto !p-0">
                <li v-for="action in content.actions" :key="action.uuid">
                  <StoryblokComponent :theme="action.theme" :size="action.size" :blok="action" />
                </li>
              </ul>
            </Slide>
            <template #addons>
              <div>
                <div class="flex flex-wrap items-center justify-start gap-x-2 gap-y-6 sm:flex-nowrap">
                  <Pagination />
                  <Navigation>
                    <template #prev>
                      <div
                        class="h-[35px] w-[35px] flex cursor-pointer items-center justify-center rounded-full bg-secondary-gray-200 ring-4 ring-secondary-gray-100/25 transition-transform duration-200 ease-out hover:scale-110 hover:ring-8"
                      >
                        <div>
                          <svg class="m-auto text-primary" width="18" height="18" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M16.62 2.99a1.25 1.25 0 0 0-1.77 0L6.54 11.3a.996.996 0 0 0 0 1.41l8.31 8.31c.49.49 1.28.49 1.77 0s.49-1.28 0-1.77L9.38 12l7.25-7.25c.48-.48.48-1.28-.01-1.76z"
                            />
                          </svg>
                        </div>
                      </div>
                    </template>
                    <template #next>
                      <div
                        class="h-[35px] w-[35px] flex cursor-pointer items-center justify-center rounded-full bg-secondary-red-100 ring-4 ring-secondary-red-100/25 transition-transform duration-200 ease-out hover:scale-110 hover:ring-8"
                      >
                        <div>
                          <svg class="text-white" width="18" height="18" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M7.38 21.01c.49.49 1.28.49 1.77 0l8.31-8.31a.996.996 0 0 0 0-1.41L9.15 2.98c-.49-.49-1.28-.49-1.77 0s-.49 1.28 0 1.77L14.62 12l-7.25 7.25c-.48.48-.48 1.28.01 1.76z"
                            />
                          </svg>
                        </div>
                      </div>
                    </template>
                  </Navigation>
                  <a href="https://www.youtube.com/watch?v=xn4gzPgnha4&ab_channel=DexonTechnology" target="_blank" rel="noopener" class="flex cursor-pointer items-center gap-x-3 sm:ml-7">
                    <span class="text-sm text-white font-semibold uppercase">Company video</span>
                    <svg class="text-white" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="12" r="9" stroke-linecap="round" /><path d="m14 12l-3 1.732v-3.464L14 12Z" /></g></svg>
                  </a>
                </div>
              </div>
            </template>
          </Carousel>
          <CardHeroComponent :card-item="cardItem" :card-title="cardTitle" :card-subtitle="cardSubtitle" />
        </div>
        <div class="relative z-20 min-h-0 w-full flex flex-col pr-0 lg:h-full lg:w-[35%] lg:overflow-hidden lg:pr-2">
          <div class="mr-auto text-left text-md text-secondary-red-100 font-semibold uppercase md:text-lg">
            {{ blok.header }}
          </div>
          <CarouselNewsArticleComponentVertical
            v-element-size="onResize"
            class="transition-[transform,opacity] duration-700 delay-500 ease-in-out"
            :categories="categories"
            :is-visible="targetIsVisible"
            :articles="get_all"
            :items-to-show="2"
            :wrap-around="true"
            :breakpoints="{
              0: { itemsToShow: 1 },
              1024: { itemsToShow: 3 },
            }"
          />
        </div>
      </div>
    </div>
    <HeroBackground :video="blok.video_url" :poster="blok.poster.filename" />
  </div>
</template>

<style>
.carousel-hero .carousel__pagination {
  @apply !mr-3
}
.carousel-hero .carousel__slide {
  @apply flex-col justify-start;
}
</style>
